import { Show } from "shared/components";
import { Verification } from "shared/types";
import { CombinedIncomeSubStep } from "../../constants";
import { IncomeStatusStep } from "./slices/IncomeStatusStep";
import { SelectIncomeTypeStep } from "./slices/SelectIncomeTypeStep";
import { CompanyDetailsStep } from "./slices/CompanyDetailsStep";
import { SupervisorDetailsStep } from "./slices/SupervisorDetailsStep";
import { PreTaxIncomeStep } from "./slices/PreTaxIncomeStep";
import { SelectDocumentTypeStep } from "./slices/SelectDocumentTypeStep";
import { DocumentUploadStep } from "./slices/DocumentUploadStep";
import { AddAnotherIncomeStep } from "./slices/AddAnotherIncomeStep";
import { NoIncomeReasonStep } from "./slices/NoIncomeReasonStep";
import { OtherIncomeDetailsStep } from "./slices/OtherIncomeDetailsStep";
import { CombinedIncomeProvider, useCombinedIncome } from "./context";
import { CombinedIncomeMode, CombinedIncomeStepData } from "./types";
import { HousingAssistanceStep } from "./slices/HousingAssistance";

interface CombinedIncomeProps {
  refetchApplication: () => void;
  applicationId: number;
  applicantId: number;
  verification?: Verification;
  mode?: CombinedIncomeMode;
  refetchVerification: () => void;
  onNextStep?: () => void;
  onComplete?: () => void;
  stepData?: CombinedIncomeStepData;
  onStepDataChange?: (patch: Partial<CombinedIncomeStepData>) => void;
  footerSlot?: HTMLElement | null;
}

const CombinedIncomeSteps = () => {
  const { subStep, mode } = useCombinedIncome();
  const isAddingNewIncome = mode === CombinedIncomeMode.AddNewIncomeSource;

  return (
    <div
      className={
        isAddingNewIncome
          ? "h-[488px] overflow-y-auto bg-background-900"
          : "h-[calc(80vh-72px)]"
      }
    >
      <Show if={subStep === CombinedIncomeSubStep.IncomeStatus}>
        <IncomeStatusStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.NoIncomeReason}>
        <NoIncomeReasonStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.SelectIncomeType}>
        <SelectIncomeTypeStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.CompanyDetails}>
        <CompanyDetailsStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.OtherIncomeDetails}>
        <OtherIncomeDetailsStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.SupervisorDetails}>
        <SupervisorDetailsStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.PreTaxIncome}>
        <PreTaxIncomeStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.SelectDocumentType}>
        <SelectDocumentTypeStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.DocumentUpload}>
        <DocumentUploadStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.AddAnotherIncome}>
        <AddAnotherIncomeStep />
      </Show>

      <Show if={subStep === CombinedIncomeSubStep.HousingAssistance}>
        <HousingAssistanceStep />
      </Show>
    </div>
  );
};

export const CombinedIncome = ({
  refetchApplication,
  applicationId,
  applicantId,
  verification,
  refetchVerification,
  mode,
  onNextStep,
  stepData,
  onStepDataChange,
  footerSlot
}: CombinedIncomeProps) => {
  return (
    <CombinedIncomeProvider
      mode={mode}
      applicationId={applicationId}
      applicantId={applicantId}
      verification={verification}
      refetchApplication={refetchApplication}
      refetchVerification={refetchVerification}
      onNextStep={onNextStep}
      stepData={stepData}
      onStepDataChange={onStepDataChange}
      footerSlot={footerSlot}
    >
      <CombinedIncomeSteps />
    </CombinedIncomeProvider>
  );
};
